import type { WerkgebiedPlace } from '../types/content'

// Werkgebiedpagina's. Eén pagina per plaats op /werkgebied/<slug>.
// Regels voor deze teksten:
// - Elke plaats eigen tekst, geen sjabloon met een andere plaatsnaam.
// - Ondernemersklimaat in algemene, controleerbare termen. Geen cijfers.
// - Afstanden en reistijden zijn benaderingen vanaf Schuit 8a, Oudkarspel.
// - Bij "Klanten in de buurt" alleen cases die echt in die omgeving zitten.
// - De losse term webdesign bewust vermijden: daarvoor is een aparte site.

export const werkgebiedPlaces: WerkgebiedPlace[] = [
  {
    slug: 'alkmaar',
    name: 'Alkmaar',
    shortName: 'Alkmaar',
    municipality: 'Gemeente Alkmaar',
    isMain: true,
    metaDescription:
      'Online marketing, SEO en maatwerk webapplicaties voor ondernemers in Alkmaar. Noord Digital zit een kwartier verderop in Oudkarspel en werkt met vaste aanspreekpunten.',
    intro:
      'Alkmaar is voor veel ondernemers in de regio de plek waar klanten zoeken, vergelijken en kiezen. Wij zitten een kwartier verderop in Oudkarspel en helpen Alkmaarse bedrijven om op dat moment gevonden te worden, er professioneel uit te zien en hun werk slimmer in te richten.',
    klimaat: [
      'Alkmaar heeft een centrumfunctie voor Noord-Holland Noord. Mensen uit Heiloo, Bergen, Dijk en Waard en verder komen er voor zorg, winkels, onderwijs en zakelijke diensten. Dat merk je online: veel zoekopdrachten in de regio eindigen op Alkmaar, ook als de zoeker er zelf niet woont.',
      'De bedrijvigheid is breed. Naast de historische binnenstad met detailhandel en horeca zijn er bedrijventerreinen zoals de Boekelermeer, een grote zorgsector en veel zakelijke dienstverleners, van adviesbureaus tot installatiebedrijven. Juist die mix maakt lokale vindbaarheid hier interessant: de concurrentie op de brede termen is groter dan in de dorpen eromheen, maar op specifieke diensten valt nog veel te winnen.',
    ],
    bereikbaarheid: {
      afstand: 'ongeveer 12 km',
      reistijd: 'ongeveer een kwartier met de auto',
      tekst:
        'Vanaf ons kantoor in Oudkarspel zijn we via de N245 snel in Alkmaar. Een kennismaking bij jou op kantoor is dus geen probleem, en voor een snelle afstemming komen we net zo makkelijk langs als dat we bellen.',
    },
    diensten: [
      {
        title: 'Lokale SEO voor Alkmaar',
        body: 'Pagina\u2019s die aansluiten op wat mensen in Alkmaar en omgeving echt zoeken, een compleet Google Bedrijfsprofiel en een site die Google aan de regio verbindt.',
        link: '/seo',
      },
      {
        title: 'Maatwerk webapplicaties',
        body: 'Voor Alkmaarse bedrijven die vastlopen in Excel, losse tools of dubbel werk bouwen we klantportalen, planningstools en koppelingen tussen systemen.',
        link: '/webapplicatie-laten-maken',
      },
      {
        title: 'De volledige samenwerking',
        body: 'Website, vormgeving, hosting, e-mail en vindbaarheid bij één partij. Eén aanspreekpunt dat je bedrijf kent, in plaats van vier leveranciers die naar elkaar wijzen.',
        link: '/#services',
      },
    ],
    cases: [
      { slug: 'gymfit-de-noord', locatie: 'Dijk en Waard' },
      { slug: 'asfamily-fitness-center', locatie: 'Heerhugowaard' },
      { slug: 'de-haar-school', locatie: 'Obdam, tussen Alkmaar en Hoorn' },
    ],
    extraSections: [
      {
        title: 'Wat online zichtbaarheid in Alkmaar anders maakt',
        body: 'In een stad met een regiofunctie zoeken mensen op twee manieren: op de stad zelf ("fysiotherapeut Alkmaar") en op hun eigen wijk of dorp. Een goede lokale aanpak dekt beide. Dat betekent een pagina per dienst waar je echt werk uit wilt halen, je werkgebied in gewone zinnen op je site, en een bedrijfsprofiel dat overal hetzelfde adres en nummer noemt. Dat werk is niet spannend, maar het wordt in de praktijk vaak half gedaan. Daar zit de ruimte.',
      },
      {
        title: 'Waarom een bureau uit de buurt',
        body: 'Je kunt je online marketing overal laten doen. Het voordeel van een partij uit de regio is dat we de plaatsnamen, de wijken en de concurrenten kennen die jouw klanten ook zien. En als er iets moet gebeuren, zit je binnen een kwartier aan tafel.',
      },
    ],
    faq: [
      {
        question: 'Komen jullie ook langs bij bedrijven in Alkmaar?',
        answer:
          'Ja. Ons kantoor ligt in Oudkarspel, ongeveer een kwartier rijden van Alkmaar. Een kennismaking of overleg bij jou op locatie plannen we net zo makkelijk als een videogesprek.',
      },
      {
        question: 'Hebben jullie ervaring met lokale SEO in Alkmaar?',
        answer:
          'We werken voor ondernemers in Alkmaar en de dorpen en steden eromheen. De aanpak voor lokale vindbaarheid is overal vergelijkbaar, maar in Alkmaar is de concurrentie op brede termen groter. Daarom beginnen we met de specifieke diensten en zoekvragen waar je het snelst resultaat mee boekt.',
      },
      {
        question: 'Kan ik bij jullie ook alleen een webapplicatie laten maken, zonder website?',
        answer:
          'Ja. Webapplicaties zijn een losse dienst. Veel bedrijven hebben al een website en zoeken alleen een oplossing voor een proces dat nu in Excel of losse tools loopt. Dat kan prima los van de rest.',
      },
    ],
    coords: { lat: 52.632, lng: 4.748 },
  },
  {
    slug: 'heerhugowaard',
    name: 'Heerhugowaard',
    shortName: 'Heerhugowaard',
    municipality: 'Gemeente Dijk en Waard',
    metaDescription:
      'Online zichtbaarheid en SEO voor ondernemers in Heerhugowaard. Noord Digital zit om de hoek in Oudkarspel, in dezelfde gemeente Dijk en Waard.',
    intro:
      'Heerhugowaard en Oudkarspel vallen sinds 2022 samen onder de gemeente Dijk en Waard. Voor ons is Heerhugowaard dus letterlijk om de hoek. We helpen ondernemers hier om lokaal beter gevonden te worden en online net zo verzorgd over te komen als in het echt.',
    klimaat: [
      'Heerhugowaard is in korte tijd uitgegroeid tot een van de grotere kernen in Noord-Holland Noord. Het stadshart trekt winkelend publiek uit de wijde omgeving en op bedrijventerreinen zoals De Vaandel zitten veel mkb-bedrijven, van vakbedrijven tot groothandels.',
      'Veel ondernemers hier werken voor klanten in de hele regio en niet alleen in Heerhugowaard zelf. Online betekent dat: gevonden worden op je eigen plaatsnaam, maar ook in Alkmaar, Langedijk en de dorpen eromheen.',
    ],
    bereikbaarheid: {
      afstand: 'ongeveer 7 km',
      reistijd: 'zo\u2019n tien minuten met de auto',
      tekst:
        'We zitten in dezelfde gemeente. Even langskomen om iets door te nemen is voor ons een kleine moeite, en je hoeft niet eerst een halve dag vrij te maken voor een afspraak.',
    },
    diensten: [
      {
        title: 'Gevonden worden in de regio',
        body: 'Je bedrijf zichtbaar maken op Heerhugowaard en op de plaatsen waar je klanten echt vandaan komen, zonder tien vrijwel identieke plaatspagina\u2019s.',
        link: '/seo',
      },
      {
        title: 'Hosting en zakelijke e-mail',
        body: 'Je site en je mail op één plek geregeld, met een vast aanspreekpunt in je eigen gemeente als er iets is.',
        link: '/hosting',
      },
      {
        title: 'Vormgeving die klopt',
        body: 'Een huisstijl die je terugziet op je site, je bus en je offertes. Voor vakbedrijven vaak net zo belangrijk als de vindbaarheid zelf.',
        link: '/vormgeving',
      },
    ],
    cases: [
      { slug: 'asfamily-fitness-center', locatie: 'Heerhugowaard' },
      { slug: 'gymfit-de-noord', locatie: 'Dijk en Waard' },
    ],
    faq: [
      {
        question: 'Werken jullie voor bedrijven in Heerhugowaard?',
        answer:
          'Ja. Heerhugowaard ligt in dezelfde gemeente als ons kantoor in Oudkarspel. AS Family Fitness Center in Heerhugowaard is een van de klanten waarvan de case op onze site staat.',
      },
      {
        question: 'Mijn klanten komen uit de hele regio. Hoe word ik overal gevonden?',
        answer:
          'Door je werkgebied in gewone zinnen op je site te noemen, een pagina per dienst te maken en je Google Bedrijfsprofiel compleet te hebben. Een aparte pagina per plaats heeft alleen zin als je daar echt iets anders over te vertellen hebt.',
      },
      {
        question: 'Kan ik eerst alleen mijn e-mail of hosting overzetten?',
        answer:
          'Ja. Je hoeft niet alles tegelijk af te nemen. Veel ondernemers beginnen met één onderdeel en breiden uit als dat bevalt.',
      },
    ],
    coords: { lat: 52.668, lng: 4.831 },
  },
  {
    slug: 'langedijk-en-oudkarspel',
    name: 'Langedijk en Oudkarspel',
    shortName: 'Langedijk',
    municipality: 'Gemeente Dijk en Waard',
    metaDescription:
      'Online marketing en SEO in Langedijk en Oudkarspel. Noord Digital heeft zijn kantoor aan de Schuit in Oudkarspel en werkt voor ondernemers in de dorpen van Langedijk.',
    intro:
      'Hier zit ons kantoor. Aan de Schuit in Oudkarspel, in de voormalige gemeente Langedijk die sinds 2022 samen met Heerhugowaard de gemeente Dijk en Waard vormt. Voor ondernemers in Oudkarspel, Noord- en Zuid-Scharwoude, Broek op Langedijk en Sint Pancras zijn we de online partner om de hoek.',
    klimaat: [
      'Langedijk heeft een lange geschiedenis in de tuinbouw. Het vroegere rijk der duizend eilanden en de Broeker Veiling herinneren daar nog aan. De bedrijvigheid is inmiddels veel breder: veel zzp\u2019ers, vakbedrijven, zorgaanbieders en kleine dienstverleners die vooral voor klanten in de directe omgeving werken.',
      'Juist voor die bedrijven is lokale vindbaarheid belangrijk. Wie in Langedijk een schilder, fysiotherapeut of hovenier zoekt, kijkt eerst naar de kaartresultaten in Google. Daar is met een compleet profiel en een site die je dorp noemt vaak al veel te winnen.',
    ],
    bereikbaarheid: {
      afstand: 'in de buurt',
      reistijd: 'binnen een paar minuten',
      tekst:
        'Ons kantoor staat aan de Schuit 8a in Oudkarspel. Je bent welkom om langs te komen, of we komen naar jou. In de dorpen van Langedijk zijn we binnen een paar minuten.',
    },
    diensten: [
      {
        title: 'Gevonden worden in je eigen dorp',
        body: 'Een compleet Google Bedrijfsprofiel, je dorp en werkgebied op je site, en reviews van klanten uit de buurt. Het werk dat voor lokale bedrijven het meeste oplevert.',
        link: '/seo',
      },
      {
        title: 'Alles bij één partij',
        body: 'Website, vormgeving, hosting en e-mail bij een bureau uit je eigen gemeente. Eén nummer om te bellen als er iets moet gebeuren.',
        link: '/#services',
      },
      {
        title: 'Slimmer werken met maatwerk',
        body: 'Voor bedrijven die groeien en merken dat planning of administratie niet meer in een spreadsheet past, bouwen we een webapplicatie op maat.',
        link: '/webapplicatie-laten-maken',
      },
    ],
    cases: [
      { slug: 'gymfit-de-noord', locatie: 'Dijk en Waard' },
      { slug: 'asfamily-fitness-center', locatie: 'Heerhugowaard' },
    ],
    faq: [
      {
        question: 'Waar zit het kantoor van Noord Digital precies?',
        answer:
          'Aan de Schuit 8a, 1724 BD Oudkarspel, in de gemeente Dijk en Waard. Plan even een afspraak als je langs wilt komen, dan staat er koffie klaar.',
      },
      {
        question: 'Is een lokaal bureau duurder dan een bureau online?',
        answer:
          'Niet per definitie. Je betaalt voor het werk, niet voor de locatie. Het verschil is dat je bij ons een vast aanspreekpunt hebt dat je bedrijf en je regio kent, en dat je binnen een paar minuten kunt langskomen.',
      },
      {
        question: 'Ik ben zzp\u2019er in Langedijk. Waar begin ik?',
        answer:
          'Meestal bij je Google Bedrijfsprofiel en een overzichtelijke site met je diensten en je werkgebied. Dat is vaak genoeg om in je eigen dorp en de dorpen eromheen gevonden te worden. Daarna kijken we of er meer nodig is.',
      },
    ],
    coords: { lat: 52.705, lng: 4.804 },
  },
  {
    slug: 'heiloo',
    name: 'Heiloo',
    shortName: 'Heiloo',
    municipality: 'Gemeente Heiloo',
    metaDescription:
      'Online marketing en SEO voor ondernemers in Heiloo. Beter gevonden worden in Heiloo en Alkmaar, met een vast aanspreekpunt uit Oudkarspel.',
    intro:
      'Heiloo ligt ingeklemd tussen Alkmaar en Castricum en veel ondernemers hier werken voor klanten in die hele strook. Wij helpen ze om daar online zichtbaar te zijn, zonder dat ze zelf uren in Google hoeven te steken.',
    klimaat: [
      'Heiloo is vooral een woonplaats met een groen karakter en een eigen station aan de lijn tussen Alkmaar en Haarlem. De bedrijvigheid bestaat voor een groot deel uit zzp\u2019ers, praktijken en kleine dienstverleners, vaak vanuit huis of een klein kantoor.',
      'Omdat Heiloo zo dicht tegen Alkmaar aan ligt, zoeken inwoners regelmatig op Alkmaar in plaats van op hun eigen plaats. Een ondernemer in Heiloo doet er dus goed aan om op beide gevonden te worden.',
    ],
    bereikbaarheid: {
      afstand: 'ongeveer 18 km',
      reistijd: 'zo\u2019n twintig minuten met de auto',
      tekst:
        'Via Alkmaar zijn we in ongeveer twintig minuten in Heiloo. Kennismaken bij jou op locatie kan, maar een eerste gesprek via video werkt voor veel zzp\u2019ers net zo goed.',
    },
    diensten: [
      {
        title: 'Gevonden worden in Heiloo en Alkmaar',
        body: 'Je werkgebied zo op je site en in je bedrijfsprofiel zetten dat je op beide plaatsnamen verschijnt.',
        link: '/seo',
      },
      {
        title: 'Een professionele basis',
        body: 'Voor zzp\u2019ers en praktijken: een verzorgde online aanwezigheid met zakelijke e-mail op je eigen domein.',
        link: '/email',
      },
      {
        title: 'Huisstijl en vormgeving',
        body: 'Een logo en huisstijl die je zelf kunt blijven gebruiken, opgeleverd als Figma-document.',
        link: '/vormgeving',
      },
    ],
    cases: [],
    faq: [
      {
        question: 'Werken jullie ook voor zzp\u2019ers in Heiloo?',
        answer:
          'Ja. Een groot deel van onze klanten heeft één tot tien medewerkers, en zzp\u2019ers horen daar gewoon bij. We kijken wat bij jouw situatie past, zonder je een groot pakket aan te praten.',
      },
      {
        question: 'Moet ik een aparte pagina voor Alkmaar maken als ik in Heiloo zit?',
        answer:
          'Niet per se. Vaak is het genoeg om je werkgebied in gewone zinnen te noemen en je bedrijfsprofiel goed in te stellen. Een aparte pagina heeft alleen zin als je er iets eigens over te vertellen hebt.',
      },
      {
        question: 'Staan er al klanten uit Heiloo op jullie site?',
        answer:
          'Nog geen case uit Heiloo zelf. De klanten die het dichtstbij zitten vind je op onze pagina over Alkmaar.',
      },
    ],
    coords: { lat: 52.6, lng: 4.7 },
  },
  {
    slug: 'bergen',
    name: 'Bergen',
    shortName: 'Bergen',
    municipality: 'Gemeente Bergen',
    metaDescription:
      'Online zichtbaarheid en SEO voor ondernemers in Bergen, Schoorl en Egmond. Gevonden worden door inwoners en bezoekers, met een partner uit de regio.',
    intro:
      'In Bergen werk je vaak voor twee soorten klanten: mensen die er wonen en mensen die er een dag, een weekend of een week zijn. Online vraagt dat om een andere aanpak dan in een stad. Wij helpen ondernemers in Bergen, Schoorl en Egmond om voor beide zichtbaar te zijn.',
    klimaat: [
      'Bergen staat bekend om zijn kunstenaarsverleden, de duinen en de kust bij Bergen aan Zee. Toerisme en recreatie spelen een grote rol, en daarmee horeca, verhuur, winkels en ambachtelijke bedrijven die voor een deel van het jaar veel bezoekers trekken.',
      'Bezoekers zoeken anders dan inwoners. Ze oriënteren zich vaak al thuis, op hun telefoon, en letten op foto\u2019s, openingstijden en reviews. Een bedrijf dat die informatie actueel heeft, wint het van een bedrijf dat alleen in het seizoen aan zijn site denkt.',
    ],
    bereikbaarheid: {
      afstand: 'ongeveer 15 km',
      reistijd: 'zo\u2019n twintig minuten met de auto',
      tekst:
        'Via Schoorl of via Alkmaar zijn we in ongeveer twintig minuten in Bergen. Voor een fotomoment of een gesprek op locatie komen we graag langs.',
    },
    diensten: [
      {
        title: 'Zichtbaar voor bezoekers',
        body: 'Een Google Bedrijfsprofiel met actuele openingstijden, echte foto\u2019s en reviews, zodat bezoekers je vinden voordat ze in Bergen aankomen.',
        link: '/seo',
      },
      {
        title: 'Vormgeving voor horeca en winkels',
        body: 'Menukaarten, flyers en social media templates in je eigen huisstijl, die je zelf kunt aanpassen als het aanbod wisselt.',
        link: '/vormgeving',
      },
      {
        title: 'Hosting die het seizoen aankan',
        body: 'Een site die snel blijft als er in de zomer veel mensen tegelijk kijken, met back-ups en monitoring.',
        link: '/hosting',
      },
    ],
    cases: [],
    faq: [
      {
        question: 'Mijn bedrijf draait vooral in het seizoen. Heeft online marketing dan zin?',
        answer:
          'Juist dan. Bezoekers oriënteren zich vaak al weken van tevoren. Wie dan met actuele informatie, foto\u2019s en reviews in beeld is, krijgt de reserveringen. Buiten het seizoen is een goed moment om dat op orde te brengen.',
      },
      {
        question: 'Werken jullie ook in Schoorl en Egmond?',
        answer:
          'Ja. Schoorl en de Egmonden vallen onder de gemeente Bergen en liggen voor ons op vergelijkbare afstand.',
      },
      {
        question: 'Kan ik mijn menukaart of aanbod zelf aanpassen?',
        answer:
          'Ja. Vormgeving leveren we op als Figma-document, zodat je teksten en prijzen zelf kunt wijzigen. Ook op je site kun je de belangrijkste onderdelen zelf bijhouden.',
      },
    ],
    coords: { lat: 52.669, lng: 4.7 },
  },
  {
    slug: 'castricum',
    name: 'Castricum',
    shortName: 'Castricum',
    municipality: 'Gemeente Castricum',
    metaDescription:
      'Online marketing en SEO voor ondernemers in Castricum, Akersloot en Limmen. Lokaal beter gevonden worden, met een vast aanspreekpunt uit de regio.',
    intro:
      'Castricum ligt tussen de duinen en de A9, op de grens van Noord-Holland Noord. Veel ondernemers hier werken zowel voor klanten in hun eigen dorp als voor klanten richting Alkmaar en Beverwijk. Wij zorgen dat ze in dat hele gebied gevonden worden.',
    klimaat: [
      'Castricum combineert een dorps centrum met duinen en strand. Naast winkels, horeca en recreatie aan de kust zijn er veel zzp\u2019ers en kleine dienstverleners, en met het station aan de lijn naar Amsterdam en Alkmaar ook veel inwoners die elders werken.',
      'Dat betekent dat lokale ondernemers concurreren met aanbieders uit een groter gebied. Wie in Castricum een vakman of adviseur zoekt, ziet in Google ook bedrijven uit Heemskerk, Uitgeest en Alkmaar. Een duidelijke lokale aanwezigheid maakt het verschil.',
    ],
    bereikbaarheid: {
      afstand: 'ongeveer 25 km',
      reistijd: 'zo\u2019n half uur met de auto',
      tekst:
        'Castricum ligt aan de zuidkant van ons werkgebied, ongeveer een half uur rijden. Een eerste gesprek doen we vaak via video, voor de start van een project komen we graag langs.',
    },
    diensten: [
      {
        title: 'Lokaal verschil maken',
        body: 'Gevonden worden in Castricum, Akersloot en Limmen, en niet wegzakken achter aanbieders uit grotere plaatsen in de buurt.',
        link: '/seo',
      },
      {
        title: 'Een verzorgde uitstraling',
        body: 'Huisstijl en vormgeving die laten zien dat je een professioneel bedrijf bent, ook als je alleen werkt.',
        link: '/vormgeving',
      },
      {
        title: 'Zakelijke e-mail op je eigen domein',
        body: 'Mailen vanaf je eigen bedrijfsnaam in plaats van een gratis adres, netjes ingericht zodat je mail niet in spam belandt.',
        link: '/email',
      },
    ],
    cases: [],
    faq: [
      {
        question: 'Werken jullie ook voor bedrijven in Akersloot en Limmen?',
        answer:
          'Ja. Akersloot en Limmen vallen onder de gemeente Castricum en liggen voor ons zelfs iets dichterbij dan Castricum zelf.',
      },
      {
        question: 'Hoe voorkom ik dat bedrijven uit Alkmaar of Heemskerk boven mij staan?',
        answer:
          'Door Google duidelijk te maken waar je zit en voor wie je werkt: je plaats en werkgebied op je site, een compleet bedrijfsprofiel en reviews van klanten uit de buurt. Garanties op een positie geven we niet, maar dit is het werk dat in de praktijk het meeste verschil maakt.',
      },
      {
        question: 'Is een half uur rijden geen bezwaar voor de samenwerking?',
        answer:
          'Nee. Het meeste overleg gaat via telefoon of video. Voor momenten waarop het beter is om aan tafel te zitten, plannen we een afspraak bij jou of bij ons in Oudkarspel.',
      },
    ],
    coords: { lat: 52.548, lng: 4.669 },
  },
  {
    slug: 'schagen',
    name: 'Schagen',
    shortName: 'Schagen',
    municipality: 'Gemeente Schagen',
    metaDescription:
      'Online marketing, SEO en webapplicaties voor ondernemers in Schagen en de Kop van Noord-Holland. Een partner uit de regio, twintig minuten verderop.',
    intro:
      'Schagen is voor de Kop van Noord-Holland wat Alkmaar is voor de regio eromheen: de plek waar mensen naartoe gaan voor winkels, diensten en zaken. Wij helpen ondernemers in Schagen en de dorpen eromheen om daar online bij te horen.',
    klimaat: [
      'Rond Schagen ligt een groot agrarisch gebied met akkerbouw, bollenteelt en veeteelt, en daaraan gekoppeld veel toeleveranciers, loonwerkers en technische bedrijven. In de stad zelf zitten detailhandel, zorg en zakelijke dienstverleners die klanten uit de hele Kop bedienen.',
      'Veel van die bedrijven zijn familiebedrijven die groeien zonder dat hun administratie of planning mee is gegroeid. Daar zit vaak meer winst in dan in extra zichtbaarheid alleen.',
    ],
    bereikbaarheid: {
      afstand: 'ongeveer 17 km',
      reistijd: 'zo\u2019n twintig minuten met de auto',
      tekst:
        'Via de N245 zijn we in ongeveer twintig minuten in Schagen. Voor een project dat dieper op je werkwijze ingaat, zoals een webapplicatie, komen we graag een keer kijken hoe het er bij jou aan toe gaat.',
    },
    diensten: [
      {
        title: 'Webapplicaties voor planning en administratie',
        body: 'Voor bedrijven die werken met losse Excel-lijsten, whiteboards of dubbele invoer bouwen we een planning- of calculatietool die past bij hoe jullie werken.',
        link: '/webapplicatie-laten-maken',
      },
      {
        title: 'Gevonden worden in de Kop',
        body: 'Zichtbaar op Schagen en op de dorpen waar je klanten zitten, met een bedrijfsprofiel en site die dat werkgebied duidelijk maken.',
        link: '/seo',
      },
      {
        title: 'Hosting en e-mail op orde',
        body: 'Je site en je mail veilig en betrouwbaar geregeld, met back-ups en een vast aanspreekpunt.',
        link: '/hosting',
      },
    ],
    cases: [],
    faq: [
      {
        question: 'Werken jullie ook voor agrarische en technische bedrijven?',
        answer:
          'Ja. Juist bij bedrijven met een eigen manier van werken kan een webapplicatie op maat veel opleveren, bijvoorbeeld voor planning, calculatie of het koppelen van bestaande systemen.',
      },
      {
        question: 'Ik zit in een dorp bij Schagen. Moet ik dan op Schagen gevonden worden?',
        answer:
          'Dat hangt af van waar je klanten zoeken. Vaak is het goed om op beide gevonden te worden: je eigen dorp en Schagen als regionale plaats. We kijken samen waar de zoekvragen liggen.',
      },
      {
        question: 'Staan er al klanten uit Schagen op jullie site?',
        answer:
          'Nog geen case uit Schagen zelf. Iets verder noordelijk hebben we Fysiotherapiepraktijk CG in Julianadorp; die case vind je op onze pagina over Den Helder.',
      },
    ],
    coords: { lat: 52.787, lng: 4.799 },
  },
  {
    slug: 'den-helder',
    name: 'Den Helder',
    shortName: 'Den Helder',
    municipality: 'Gemeente Den Helder',
    metaDescription:
      'Online marketing, SEO en webapplicaties voor ondernemers in Den Helder en Julianadorp. Met een case uit Julianadorp als voorbeeld van ons werk.',
    intro:
      'Den Helder ligt op de kop van Noord-Holland en heeft een economie die je nergens anders in de regio ziet. Wij werken voor ondernemers in Den Helder en Julianadorp, onder meer voor Fysiotherapiepraktijk CG.',
    klimaat: [
      'Den Helder is de thuishaven van de Koninklijke Marine en heeft een sterke maritieme en offshore sector, met veel technische bedrijven en toeleveranciers. Daarnaast zijn zorg, onderwijs en detailhandel belangrijk, en is Julianadorp met zijn kust en recreatie in de zomer een trekpleister.',
      'Door de ligging aan de kop zoeken inwoners vaak dicht bij huis. Een bedrijf dat lokaal goed zichtbaar is, heeft hier minder concurrentie van buiten dan in de plaatsen rond Alkmaar.',
    ],
    bereikbaarheid: {
      afstand: 'ongeveer 40 km',
      reistijd: 'zo\u2019n veertig minuten met de auto',
      tekst:
        'Via de N9 zijn we in ongeveer veertig minuten in Den Helder. Het meeste overleg doen we op afstand, voor een kennismaking of een belangrijk moment in het project komen we naar je toe.',
    },
    diensten: [
      {
        title: 'Lokale vindbaarheid',
        body: 'Gevonden worden in Den Helder en Julianadorp op de diensten waar je werk uit wilt halen, met een site die elke dienst een eigen plek geeft.',
        link: '/seo',
      },
      {
        title: 'Webapplicaties en koppelingen',
        body: 'Voor technische en zorgbedrijven met veel processen: klantportalen, planningstools en koppelingen tussen bestaande systemen.',
        link: '/webapplicatie-laten-maken',
      },
      {
        title: 'Hosting en beheer',
        body: 'Je site veilig en actueel gehouden, zodat je daar zelf niet meer naar om hoeft te kijken.',
        link: '/hosting',
      },
    ],
    cases: [{ slug: 'fysiotherapiepraktijk-cg', locatie: 'Julianadorp' }],
    faq: [
      {
        question: 'Werken jullie echt voor bedrijven zo ver noordelijk?',
        answer:
          'Ja. Fysiotherapiepraktijk CG in Julianadorp is een van onze klanten. Veertig minuten rijden is voor een samenwerking geen bezwaar, zeker omdat het meeste overleg op afstand gaat.',
      },
      {
        question: 'Hebben jullie ervaring met zorgpraktijken?',
        answer:
          'Ja. Voor Fysiotherapiepraktijk CG bouwden we een uitgebreide praktijksite met een pagina per behandeling. De volledige case staat op onze site.',
      },
      {
        question: 'Kunnen jullie een webapplicatie koppelen aan onze bestaande software?',
        answer:
          'Vaak wel. Veel pakketten bieden een koppeling waarmee gegevens heen en weer kunnen. Wat precies mogelijk is, zoeken we per situatie uit voordat er iets gebouwd wordt.',
      },
    ],
    coords: { lat: 52.958, lng: 4.76 },
  },
  {
    slug: 'hoorn',
    name: 'Hoorn',
    shortName: 'Hoorn',
    municipality: 'Gemeente Hoorn',
    metaDescription:
      'Online marketing en SEO voor ondernemers in Hoorn en West-Friesland. Beter gevonden worden en professioneel overkomen, met een partner uit Noord-Holland Noord.',
    intro:
      'Hoorn is het centrum van West-Friesland. Ondernemers hier bedienen klanten uit een groot gebied, van Enkhuizen tot Obdam. Wij helpen ze om in die hele regio online zichtbaar te zijn en er professioneel uit te zien.',
    klimaat: [
      'Hoorn is een historische havenstad met een levendige binnenstad vol winkels en horeca. Daarnaast is het de regionale plaats voor zorg, onderwijs en zakelijke dienstverlening in West-Friesland, met veel mkb op de bedrijventerreinen aan de rand van de stad.',
      'West-Friesland heeft een sterke eigen identiteit, en klanten kiezen graag voor een bedrijf uit de buurt. Online betekent dat: je plaats en je regio duidelijk laten zien, en bewijs van werk bij mensen die ze herkennen.',
    ],
    bereikbaarheid: {
      afstand: 'ongeveer 30 km',
      reistijd: 'zo\u2019n half uur met de auto',
      tekst:
        'Via Heerhugowaard en de N194 zijn we in ongeveer een half uur in Hoorn. Onderweg ligt Obdam, waar De Haar School zit, een van onze klanten.',
    },
    diensten: [
      {
        title: 'Zichtbaar in West-Friesland',
        body: 'Gevonden worden op Hoorn en op de plaatsen in de regio waar je klanten vandaan komen.',
        link: '/seo',
      },
      {
        title: 'Vormgeving en huisstijl',
        body: 'Een uitstraling die past bij je bedrijf, doorgetrokken van je site tot je gevel en bedrijfskleding.',
        link: '/vormgeving',
      },
      {
        title: 'De volledige samenwerking',
        body: 'Website, hosting, e-mail en vindbaarheid bij één partij, met een vast aanspreekpunt dat je bedrijf kent.',
        link: '/#services',
      },
    ],
    cases: [{ slug: 'de-haar-school', locatie: 'Obdam, tussen Alkmaar en Hoorn' }],
    faq: [
      {
        question: 'Werken jullie ook in West-Friesland?',
        answer:
          'Ja. Hoorn en de omliggende plaatsen horen bij ons werkgebied. De Haar School in Obdam, tussen Alkmaar en Hoorn, is een van onze klanten.',
      },
      {
        question: 'Mijn klanten komen uit heel West-Friesland. Hoe pak ik dat aan?',
        answer:
          'Door je werkgebied duidelijk op je site en in je bedrijfsprofiel te noemen, een pagina per dienst te maken en reviews te verzamelen van klanten uit de regio. Plaatspagina\u2019s maken we alleen als er per plaats echt iets te vertellen is.',
      },
      {
        question: 'Moet ik voor een gesprek naar Oudkarspel komen?',
        answer:
          'Nee. We komen naar jou toe, of we spreken via video. Wil je een keer bij ons binnenkijken, dan ben je natuurlijk welkom.',
      },
    ],
    coords: { lat: 52.642, lng: 5.06 },
  },
]

// Ligging van ons kantoor, gebruikt als middelpunt op het regiokaartje.
export const officeCoords = { lat: 52.7089, lng: 4.8043 }

export const getWerkgebiedPlace = (slug: string): WerkgebiedPlace | undefined =>
  werkgebiedPlaces.find((place) => place.slug === slug)
